import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  author,
  canonical,
  noindex = false,
  nofollow = false,
  structuredData,
  breadcrumbs,
  alternateLanguages,
  article,
  children
}) => {
  const { t, i18n } = useTranslation();
  
  const currentLang = i18n.language || 'fr';
  const siteName = t('seo.siteName', 'FlowDino');
  const defaultDescription = t('seo.defaultDescription', 'Solution complète pour la gestion et le partage d\'articles automatisé sur différents sites Vinted, Leboncoin. Optimisez votre workflow de publication avec notre plateforme innovante.');
  const defaultKeywords = t('seo.defaultKeywords', 'FlowDino, gestion articles, partage contenu, publication, workflow, plateforme');
  
  const baseUrl = process.env.REACT_APP_BASE_URL || 'https://www.flowdino.com';
  const fullUrl = url ? `${baseUrl}${url}` : baseUrl;
  const defaultImage = `${baseUrl}/images/og-flowdino-1200x630.jpg`;
  
  const seoTitle = title ? `${title} | ${siteName}` : siteName;
  const seoDescription = description || defaultDescription;
  const seoKeywords = keywords || defaultKeywords;
  const seoImage = image || defaultImage;
  
  const robotsContent = [
    noindex ? 'noindex' : 'index',
    nofollow ? 'nofollow' : 'follow'
  ].join(',');
  
  return (
    <Helmet>
      {/* Titre et métadonnées de base */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="author" content={author || siteName} />
      <meta name="robots" content={robotsContent} />
      <meta name="language" content={currentLang} />
      
      {/* URL canonique */}
      <link rel="canonical" href={canonical || fullUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={currentLang === 'fr' ? 'fr_FR' : 'en_US'} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      <meta name="twitter:creator" content="@FlowDino" />
      
      {/* Métadonnées supplémentaires pour le référencement */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#2563eb" />
      <meta name="msapplication-TileColor" content="#2563eb" />
        {/* Hreflang pour le multilangue */}
      {/* Note: L'application gère les langues via i18n, pas via des routes séparées */}
      <link rel="alternate" hreflang="fr" href={fullUrl} />
      <link rel="alternate" hreflang="en" href={fullUrl} />
      <link rel="alternate" hreflang="x-default" href={fullUrl} />
      
      {/* Hreflang supplémentaires si fournis */}
      {alternateLanguages && alternateLanguages.map(alt => (
        <link 
          key={alt.lang} 
          rel="alternate" 
          hreflang={alt.lang} 
          href={alt.url} 
        />
      ))}
        {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": siteName,
          "description": seoDescription,
          "url": baseUrl,
          "potentialAction": {
            "@type": "SearchAction",
            "target": `${baseUrl}/articles?q={search_term_string}`,
            "query-input": "required name=search_term_string"
          }
        })}
      </script>
      
      {/* Schema.org supplémentaire */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Breadcrumbs Schema */}
      {breadcrumbs && breadcrumbs.length > 1 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbs.map((item, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": item.name,
              "item": item.url.startsWith('http') ? item.url : `${baseUrl}${item.url}`
            }))
          })}
        </script>
      )}
      
      {/* Article Schema */}
      {article && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title || seoTitle,
            "description": article.description || seoDescription,
            "author": {
              "@type": "Person",
              "name": article.author || author || siteName
            },
            "datePublished": article.publishedAt,
            "dateModified": article.updatedAt || article.publishedAt,
            "image": article.image || seoImage,
            "url": fullUrl,
            "publisher": {
              "@type": "Organization",
              "name": siteName,
              "logo": {
                "@type": "ImageObject",
                "url": `${baseUrl}/react/images/logo.png`
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": fullUrl
            }
          })}
        </script>
      )}
      
      {/* Contenu personnalisé */}
      {children}
    </Helmet>
  );
};

export default SEO;
